import axios from 'axios';
import { colors } from '../styles/theme';

// 상대경로다. 프론트가 서빙된 호스트의 /api 를 부른다 — EC2 든 OCI 든 그 서버의 백엔드를 본다.
// 전에는 https://soopooding.com/api 를 박아 둬서 OCI 에 올려도 EC2 를 봤다.
// 로컬 개발(localhost:3000)은 package.json 의 "proxy" 가 /api 를 운영으로 넘긴다.
const api = axios.create({
  baseURL: '/api',
  timeout: 30000, // 기본 30초
});

// 토큰 자동 추가
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// 401 에러시 Refresh Token으로 재발급 (동시 요청은 단일 refresh 공유)
let refreshPromise = null;

function clearAuthAndRedirect() {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  window.location.href = '/login';
}

api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('refreshToken');
      // 비로그인 상태(토큰 없음): 공개 열람 중일 수 있으므로 강제 이동 없이 조용히 실패
      if (!refreshToken) {
        return Promise.reject(error);
      }
      try {
        // 이미 refresh가 진행 중이면 그 결과를 공유
        if (!refreshPromise) {
          // 인터셉터 재귀를 피하려 api 인스턴스가 아닌 axios 를 쓴다. 그래서 baseURL 을 안 타므로
          // 여기도 상대경로여야 한다 — 절대 URL 이면 OCI 에서 리프레시만 EC2 로 간다.
          refreshPromise = axios
            .post('/api/auth/refresh', { refreshToken })
            .then(res => {
              const newToken = res.data.accessToken;
              localStorage.setItem('token', newToken);
              return newToken;
            })
            .finally(() => { refreshPromise = null; });
        }
        const newToken = await refreshPromise;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (e) {
        clearAuthAndRedirect();
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

// ===== 인증 =====
export const signUp = (data) => api.post('/auth/signup', data);
export const login = (data) => api.post('/auth/login', data);
export const logout = (refreshToken) => api.post('/auth/logout', { refreshToken });

// ===== 공고 =====
export const getStats = () => api.get('/announcements/stats');
export const getAnnouncements = (params) => api.get('/announcements', { params });
export const getAnnouncementDetail = (id) => api.get(`/announcements/${id}`);
export const getAnnouncementDetails = (ids) => Promise.all(ids.map(id => getAnnouncementDetail(id)));
export const getAnnouncementSummary = (id) => api.get(`/announcements/${id}/summary`);
export const getAnnouncementHistory = (id) => api.get(`/announcements/${id}/history`);

// ===== 조건 =====
export const saveCondition = (data) => api.post('/user/condition', data);
export const getCondition = () => api.get('/user/condition');

// ===== 계정 =====
export const updateNickname = (nickname) => api.put('/user/nickname', { nickname });
export const updatePassword = (currentPassword, newPassword) => api.put('/user/password', { currentPassword, newPassword });
export const clearRecommendCache = () => api.post('/user/recommend/cache/clear');
export const getDiagnosis = () => api.get('/user/diagnosis');
export const getStrategy = () => api.get('/user/strategy');
export const getChatLogs = (page = 0, onlyZero = false) =>
  api.get('/admin/chat-logs', { params: { page, size: 20, onlyZero } });
export const getChatLogStats = () => api.get('/admin/chat-logs/stats');
export const getTodayAnnouncements = () => api.get('/announcements/today');
// 지역 칩용 건수 — 목록과 같은 조건으로 센다 (칩 숫자와 클릭 결과 불일치 방지)
export const getSidoCounts = (params) => api.get('/announcements/sido-counts', { params });
export const getSigungus = (sido) => api.get('/announcements/sigungus', { params: sido ? { sido } : {} });
export const getPopularQuestions = () => api.get('/chat/popular');
export const getChatKeywords = () => api.get('/admin/chat-logs/keywords');

// ===== AI 추천 - timeout 0 (무제한, 30초~1분 소요) =====
export const getRecommend = (mode = 'MATCH') => api.post('/recommend', {}, { params: { mode }, timeout: 0 });
export const getRecommendHistory = () => api.get('/recommend/history');
export const getRemaining = () => api.get('/recommend/remaining');
export const saveFeedback = (announcementId, data) => api.post(`/feedback/${announcementId}`, data);

// ===== 챗봇 =====
export const chat = (message) => api.post('/chat', { message }, { timeout: 60000 });

// ===== 관심공고 =====
export const toggleFavorite = (announcementId) => api.post(`/favorites/${announcementId}`);
export const checkFavorite = (announcementId) => api.get(`/favorites/${announcementId}`);
export const getFavorites = () => api.get('/favorites');
export const getFavoriteSchedules = () => api.get('/favorites/schedules');

// ===== 신고 =====
export const submitReport = (data) => api.post('/reports', data);
export const getReports = () => api.get('/admin/reports');
export const resolveReportApi = (id) => api.put(`/admin/reports/${id}/resolve`);

// ===== 관리자 =====
export const getAdminStats = () => api.get('/admin/stats');
export const getAdminUsers = () => api.get('/admin/users');
export const getPendingUsers = () => api.get('/admin/users/pending');
export const toggleUser = (userId) => api.put(`/admin/users/${userId}/toggle`);
export const changeUserRole = (userId, role) => api.put(`/admin/users/${userId}/role?role=${role}`);
export const approveUser = (userId) => api.put(`/admin/users/${userId}/approve`);
export const rejectUser = (userId) => api.put(`/admin/users/${userId}/reject`);
export const deleteUser = (userId) => api.delete(`/admin/users/${userId}`);

// ===== 배치 - timeout 0 (무제한) =====
export const runFullPipeline = () => api.post('/admin/pipeline/full', {}, { timeout: 0 });
export const runEmbedOnly = () => api.post('/admin/pipeline/embed-only', {}, { timeout: 0 });
export const getBatchStatus = () => api.get('/admin/batch/status');

// ===== 설정 =====
export const getSettings = () => api.get('/admin/settings');
export const toggleAutoApprove = () => api.post('/admin/settings/auto-approve/toggle');

// ===== 토스트 =====
export const showToast = (message, type = 'success') => {
  const existing = document.getElementById('spd-toast');
  if (existing) existing.remove();

  const toast = document.createElement('div');
  toast.id = 'spd-toast';
  const bgColor = type === 'success' ? colors.greenText
    : type === 'error' ? colors.red
    : type === 'warning' ? colors.orangeText
    : colors.accent;

  toast.style.cssText = `
    position: fixed; bottom: 80px; left: 50%; transform: translateX(-50%);
    background: ${bgColor}; color: white; padding: 14px 24px;
    border-radius: 12px; font-size: 15px; font-weight: bold;
    box-shadow: 0 4px 20px rgba(0,0,0,0.2); z-index: 9999;
    max-width: 90%; text-align: center; white-space: pre-wrap;
    animation: slideUp 0.3s ease;
  `;
  toast.textContent = message;

  document.body.appendChild(toast);
  setTimeout(() => toast.remove(), 3000);
};